





















































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { IBrandGateway } from "@/models/gateways";
import { GatewayContext, GatewayStoreType } from "@/data/enums/gateway";
import { BrandConfigKeys } from "@/data/constants";
import { IInvoice, IInvoiceUnpaid } from "@/models/invoices";
import { AdminRoutes, ClientRoutes } from "@/data/enums/router";
import Context from "@/mixins/context";
import _ from "@/boot/lodash";
import { SnackbarProgrammatic as $snackbar } from "buefy";
import { DataModules } from "@/store/modules/data";

@Component
export default class GatewayComponent extends Mixins(Context) {
  // Props

  @Prop({ required: true })
  public brandGateway!: IBrandGateway;

  @Prop({ required: true })
  public gatewayContext!: GatewayContext;

  @Prop({ default: true })
  public isRenderless!: boolean;

  @Prop({ default: null })
  public unpaidInvoices!: IInvoiceUnpaid;

  // Data

  public form: any = {};
  public gatewayData: {
    type?: string;
    data?: Record<string, any>;
    dataFunc?: Promise<any>;
  } | null = null;

  public created() {
    if (this.canStoreOnPayment) {
      this.$set(this.form, "store_on_payment", true);
      this.$set(this.form, "store_on_payment_auto_payment", true);
    }
  }

  /**
   * @name isEmpty
   * @desc Returns true if there's no content to render
   */

  public get isEmpty(): boolean {
    if (this.isAddContext && !this.canBeStored) return false;
    return !this.$slots["instructions"] && this.isRenderless;
  }

  /**
   * @name isAddContext
   * @desc Returns true if in the context of adding an adhoc payment method
   */

  public get isAddContext(): boolean {
    return this.gatewayContext === GatewayContext.ADD;
  }

  /**
   * @name isExternal
   * @desc Returns true if payment is processed off-site via a redirect flow
   */

  public get isExternal(): boolean {
    return !!this.brandGateway.gateway?.gateway_provider?.external_payment;
  }

  /**
   * @name canStoreOnPayment
   * @desc Returns true if payment details can be stored during the payment request
   */

  public get canStoreOnPayment(): boolean {
    if (this.isAddContext || !this.canBeStored) return false;
    if (this.$store.getters.isGuestContext) return false;
    return !!this.brandGateway.gateway?.store_on_payment;
  }

  public get showStoreOnPaymentControls() {
    if (this.forceCardStorage && this.forceAutoPayment) return false;
    if (!this.gatewayData) return false;
    const dataKeys = Object.keys(this.gatewayData?.data || {});
    // Check if component has already captured store/auto_payment flags
    if (dataKeys.includes("store") && dataKeys.includes("auto_payment"))
      return false;
    return this.canStoreOnPayment;
  }

  /**
   * @name canBeStored
   * @desc Returns true if gateway supports storing of payment details in the
   * current context
   */

  public get canBeStored() {
    const {
      is_stored,
      gateway_provider,
      store_on_payment,
      store_outside_payment
    } = this.brandGateway?.gateway;
    const { store_type } = gateway_provider;
    if (!is_stored) return false;
    if (store_type === GatewayStoreType.NONE) return false;
    if (this.isAddContext && store_outside_payment) return true;
    if (this.isAddContext && store_on_payment) return false;
    return true;
  }

  /**
   * @name forceCardStorage
   * @desc Returns true if detail storage is enforced by the brand
   */

  public get forceCardStorage(): boolean {
    return (
      (this.$store.getters["brand/config"][
        BrandConfigKeys.BILLING_GATEWAY_FORCE_CARD_STORAGE
      ] ??
        false) ||
      (this.brandGateway.gateway?.store_on_payment_force ?? false)
    );
  }

  /**
   * @name forceAutoPayment
   * @desc Returns true if uatopayment is enforced by the brand
   */

  public get forceAutoPayment(): boolean {
    return (
      this.$store.getters["brand/config"][
        BrandConfigKeys.BILLING_GATEWAY_FORCE_AUTO_PAYMENT
      ] ?? false
    );
  }

  /**
   * @name computedData
   * @desc Returns gateway data merged with any global/common properties
   */

  public get computedData() {
    if (this.gatewayData === null) return null;
    if (this.isAddContext && !this.canBeStored) return null;
    return _.merge({}, this.gatewayData, { data: this.form });
  }

  /**
   * @name relayGatewayData
   * @desc A wrapper function to relay gateway data via scoped slots
   */

  public relayGatewayData(data) {
    this.gatewayData = data;
  }

  public async payInvoice() {
    this.$emit("close");
    const modal = await this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import("@/components/app/global/payments/invoicePaymentModal.vue"),
        props: {
          invoice: this.unpaidInvoices.invoice
        },
        width: 740,
        canCancel: ["button"],
        events: {
          success: (invoice: IInvoice) => {
            // Redirect to invoice
            this.$router
              .push(
                this.$store.getters[`data/${DataModules.INVOICES}/getRoute`](
                  invoice.id,
                  invoice.client_id
                )
              )
              .catch(err => err);
            this.$emit("payment-success");
            modal.close();
          }
        },
        fail: () => {
          $snackbar.open({
            message: this.$t("_.payment_failed") as string,
            type: "is-warning"
          });
        }
      }
    });
  }

  public get noInvoicesToPay() {
    return !this.unpaidInvoices.total;
  }

  public get unpaidInvoicesCount() {
    return this.unpaidInvoices.total || 0;
  }

  public get unpaidInvoicesRoute() {
    return {
      name: this.isAdmin
        ? AdminRoutes.CLIENT_BILLING_UNPAID_INVOICES
        : ClientRoutes.INVOICES_UNPAID,
      params: {
        clientId: this.unpaidInvoices.invoice?.client_id
      }
    };
  }

  public get cannotBeStoredMarkdown() {
    const { name, name_translated } = this.brandGateway?.gateway;
    return this.$tc(
      "_sentence.payments.method_can_be_used_but_not_stored_plural",
      this.unpaidInvoicesCount,
      {
        gatewayName: name_translated || name,
        unpaidCount: this.unpaidInvoicesCount,
        invoiceNum: this.unpaidInvoices?.invoice?.number,
        absoluteDueDate: this.$moment(this.unpaidInvoices?.invoice?.due_date)
          .utc()
          .format(this.$t("_datetime.date.long").toString())
      }
    );
  }

  @Watch("computedData", { deep: true, immediate: true })
  public onComputedDataChange() {
    this.$emit("input", this.computedData);
  }
}
